export const LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
};

export const LOCALE_DIR: Record<Locale, 'ltr' | 'rtl'> = {
  en: 'ltr',
  ar: 'rtl',
};

/**
 * A message that varies by count. Arabic uses all six CLDR categories; English
 * uses two. Every catalogue must supply the categories its own language needs,
 * and `other` always, so a lookup can never fall through to nothing.
 */
export interface Plural {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}

/**
 * Every string the app can show. Both catalogues are typed against this, so a
 * key missing from Arabic is a compile error rather than an English word
 * appearing mid-sentence.
 */
export interface Messages {
  'app.tagline': string;
  'app.blurb': string;
  'app.streakLabel': string;
  'app.streakOwner': string;
  'app.provisionalName': string;

  'welcome.start.kids': string;
  'welcome.start.adult': string;
  'welcome.privacy': string;

  'onboarding.title': string;
  'onboarding.nameLabel': string;
  'onboarding.namePlaceholder': string;
  'onboarding.ageLabel': string;
  'onboarding.years': Plural;
  'onboarding.yearsUnit': Plural;
  /** The open-ended top step of the age stepper, e.g. "14+". */
  'onboarding.agePlus': string;
  /** The same step spelled out in a sentence, e.g. "14+ years old". */
  'onboarding.yearsPlus': string;
  'onboarding.younger': string;
  'onboarding.older': string;
  'onboarding.continue': string;
  'onboarding.privacy': string;
  'onboarding.verdict.kids': string;
  'onboarding.verdict.adult': string;
  'onboarding.verdictNote.kids': string;
  'onboarding.verdictNote.adult': string;

  'home.heroEyebrow': string;
  /** The unit beside the big streak numeral, which counts prayers. */
  'home.prayersUnit': Plural;
  /** The same run in whole days, shown beside it. */
  'home.days': Plural;
  'home.recorded': string;
  'home.today': string;
  /** Tag on the prayer whose time it is now. */
  'home.now': string;
  'home.markLink': string;
  'home.reward.title': Plural;
  'home.reward.sub': string;
  'home.reward.litTitle': string;
  'home.reward.litSub': string;
  'home.reward.emptyTitle': string;
  'home.reward.emptySub': string;
  'home.hadith': string;
  'home.hadithSource': string;

  'prayer.fajr': string;
  'prayer.dhuhr': string;
  'prayer.asr': string;
  'prayer.maghrib': string;
  'prayer.isha': string;
  'prayer.ar.fajr': string;
  'prayer.ar.dhuhr': string;
  'prayer.ar.asr': string;
  'prayer.ar.maghrib': string;
  'prayer.ar.isha': string;

  'note.later': string;
  'note.missed': string;
  'note.takbirah': string;
  'note.jamaahOnTime': string;
  'note.jamaahLate': string;
  'note.onTime': string;
  'note.late': string;

  'quality.prayed.label': string;
  'quality.prayed.short': string;
  'quality.prayed.kids': string;
  'quality.prayed.adult': string;
  'quality.onTime.label': string;
  'quality.onTime.short': string;
  'quality.onTime.kids': string;
  'quality.onTime.adult': string;
  'quality.jamaah.label': string;
  'quality.jamaah.short': string;
  'quality.jamaah.kids': string;
  'quality.jamaah.adult': string;
  'quality.takbirah.label': string;
  'quality.takbirah.short': string;
  'quality.takbirah.kids': string;
  'quality.takbirah.adult': string;
  'quality.needsJamaah': string;
  'quality.ar.prayed': string;
  'quality.ar.onTime': string;
  'quality.ar.jamaah': string;
  'quality.ar.takbirah': string;

  'log.adhan': string;
  'log.save': string;
  'log.saved': string;
  'log.makeUp.kids': string;
  'log.makeUp.adult': string;
  'log.allFour.title': string;
  'log.allFour.sub': string;
  'log.saveFailed': string;
  'log.privacy': string;
  'log.close': string;
  'log.onDay': string;

  'settings.title': string;
  'settings.back': string;
  'settings.whoIsPraying': string;
  'settings.name': string;
  'settings.nameUnset': string;
  'settings.age': string;
  'settings.lookAndFeel': string;
  'settings.byAge': string;
  'settings.byHand': string;
  'settings.fun': string;
  'settings.funSub': string;
  'settings.plain': string;
  'settings.backToAge': string;
  'settings.plainSub': string;
  'settings.lightDark': string;
  'settings.day': string;
  'settings.night': string;
  'settings.auto': string;
  'settings.autoResolved': string;
  'settings.autoNote': string;
  'settings.themeColour': string;
  'settings.accentNote': string;
  'settings.parentGate': string;
  'settings.language': string;
  'settings.prayerTimes': string;
  'settings.sampleTimes': string;
  'settings.useLocation': string;
  'settings.updateLocation': string;
  'settings.locating': string;
  'settings.locationDenied': string;
  'settings.locationSet': string;
  'settings.method': string;
  'settings.methodNote': string;
  'settings.madhab': string;
  'settings.madhab.shafi': string;
  'settings.madhab.hanafi': string;

  'accent.brass': string;
  'accent.terracotta': string;
  'accent.rose': string;
  'accent.olive': string;
  'accent.lapis': string;
  'accent.emerald': string;

  'nav.label': string;
  'nav.home': string;
  'nav.report': string;

  'report.previous': string;
  'report.next': string;
  'report.theseDays': string;
  'report.recorded': string;
  'report.recordedLabel': string;
  'report.fullDays': string;
  'report.perfect': string;
  'report.bestRun': string;
  'report.statistics': string;
  'report.rangeLabel': string;
  /** A number of days, on its own: the range buttons and a record's length. */
  'report.dayCount': Plural;
  'report.statsNote': string;
  'report.statsTotal': Plural;
  'report.qualityShare': string;
  'report.eachPrayer': string;
  'report.eachPrayerNote': string;
  'report.scaleWeek': string;
  'report.scaleDays': string;
  'report.inARow': Plural;
  'report.longestRun': string;
  'report.prayerQuality': string;
  /** The same, for a bar carrying a record star. */
  'report.prayerQualityBest': string;
  'report.starNote': string;
  'report.empty': string;
  'report.legend': string;
  'report.editHint': string;
  'report.cellLabel': string;
}

export type MessageKey = keyof Messages;
